import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Target, TrendUp, Users, Sparkle, ChartBar, Gear, ArrowClockwise,
  Trophy, Warning, FacebookLogo, Heart, ChatCircle, ShareFat, ArrowUpRight,
} from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";
import { CampaignSelect } from "../components/CampaignSelect";

const GOLD = "#D4A24C";
const RED = "#D90429";

const GOAL_FIELDS = [
  { key: "trials_per_month", label: "Essais réservés / mois" },
  { key: "new_enrollments_summer", label: "Nouvelles inscriptions (été)" },
  { key: "trust_posts_month", label: "Posts Confiance & Évolution / mois" },
  { key: "engagement_total_month", label: "Engagement total / mois" },
];

export default function MarketingDashboardModule() {
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loadingPerf, setLoadingPerf] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [goals, setGoals] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [rollupBusy, setRollupBusy] = useState(false);

  const loadRollup = async (cid) => {
    if (!cid) { setRollup(null); return; }
    setRollupBusy(true);
    try {
      const { data } = await api.get(`/planner/campaign-rollup?campaign_id=${cid}`);
      setRollup(data);
    } catch { toast.error("Erreur de chargement de la campagne"); setRollup(null); }
    finally { setRollupBusy(false); }
  };
  const onPickCampaign = (v) => { setCampaignId(v); loadRollup(v); };

  const exportRollupPdf = async () => {
    if (!campaignId) return;
    try {
      const res = await api.get(`/planner/campaign-rollup/pdf?campaign_id=${campaignId}`, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      window.open(href, "_blank");
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch { toast.error("Export PDF impossible"); }
  };


  const load = async () => {
    try {
      const { data } = await api.get("/marketing/dashboard");
      setData(data);
    } catch { toast.error("Erreur de chargement du tableau de bord"); }
  };

  const loadPerf = async (withAi = false) => {
    withAi ? setAiBusy(true) : setLoadingPerf(true);
    try {
      const { data } = await api.get(`/marketing/dashboard/post-performance${withAi ? "?ai=true" : ""}`);
      setPerf(data);
    } catch { toast.error("Erreur lors de l'analyse des publications"); }
    finally { setLoadingPerf(false); setAiBusy(false); }
  };

  useEffect(() => { load(); loadPerf(false); }, []);

  const openGoals = async () => {
    const { data } = await api.get("/marketing/dashboard/goals");
    setGoals(data.goals);
    setGoalsOpen(true);
  };

  const saveGoals = async () => {
    try {
      await api.put("/marketing/dashboard/goals", goals);
      toast.success("Cibles mises à jour");
      setGoalsOpen(false);
      load();
    } catch { toast.error("Erreur"); }
  };

  // Inject live engagement actual into objective card
  const objectives = (data?.objectives || []).map((o) =>
    o.key === "engagement" && perf?.configured
      ? { ...o, actual: perf.engagement_total_month, progress_pct: Math.min(Math.round((perf.engagement_total_month / o.target) * 100), 100) }
      : o
  );

  return (
    <div data-testid="marketing-dashboard-page" className="min-h-screen bg-zinc-950">
      {/* HEADER */}
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Reconquête & Croissance"
          icon={ChartBar}
          breadcrumbs={[{ label: "Marketing & contenu" }, { label: "Tableau Marketing" }]}
          subtitle={data?.period ? `Mesurer pour décider · Période : ${data.period?.month} · Été : ${data.period?.summer}` : "Mesurer pour décider. Regagner la confiance, montrer l'évolution, attirer les familles."}
          actions={
            <>
              <button data-testid="btn-edit-goals" onClick={openGoals} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-zinc-700 hover:border-zinc-500 text-sm text-zinc-200 transition-colors">
                <Gear size={16} /> Mes cibles
              </button>
              <button data-testid="btn-refresh" onClick={() => { load(); loadPerf(false); }} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-zinc-950 font-semibold transition-colors" style={{ background: GOLD }}>
                <ArrowClockwise size={16} /> Actualiser
              </button>
            </>
          }
        />
      </div>

      <div className="px-8 lg:px-12 py-8 space-y-12">
        {/* FILTRE PAR CAMPAGNE */}
        <section data-testid="campaign-filter-section">
          <div className="hard-border p-5 bg-white">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <CampaignSelect
                  value={campaignId}
                  onChange={onPickCampaign}
                  label="🔎 Filtrer par campagne (voir promos + contenus + publications liés)"
                  labelClassName="ticker text-muted-foreground block mb-2"
                  className="w-full hard-border px-4 py-2.5 bg-white text-zinc-900 text-sm"
                  testid="dashboard-campaign-select"
                />
              </div>
              {campaignId && (
                <button data-testid="clear-campaign-filter" onClick={() => onPickCampaign(null)}
                  className="ticker px-4 py-2.5 hard-border bg-secondary/40 hover:bg-secondary text-sm whitespace-nowrap">
                  ✕ Tout afficher
                </button>
              )}
              {campaignId && rollup && (
                <button data-testid="export-campaign-pdf" onClick={exportRollupPdf}
                  className="ticker px-4 py-2.5 hard-border text-white text-sm whitespace-nowrap" style={{ background: RED }}>
                  📄 Exporter le bilan PDF
                </button>
              )}
            </div>

            {rollupBusy && <div className="mt-4 text-sm text-muted-foreground">Chargement de la campagne…</div>}

            {rollup && !rollupBusy && (
              <div className="mt-5 space-y-5" data-testid="campaign-rollup">
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  <RollupStat label="Promos" value={rollup.counts.promos} />
                  <RollupStat label="Contenus" value={rollup.counts.contents} />
                  <RollupStat label="Publications" value={rollup.counts.publications} />
                  <RollupStat label="Prospects" value={rollup.resultats?.prospects ?? 0} accent />
                  <RollupStat label="Essais" value={rollup.resultats?.essais ?? 0} accent />
                  <RollupStat label="Inscriptions" value={rollup.resultats?.inscriptions ?? 0} accent />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <RollupList title="🏷️ Promos" testid="rollup-promos" items={rollup.promos.map((p) => ({
                    key: p.name, main: p.name, sub: `${p.discount_value}${p.discount_type === "percent" ? "%" : "$"} · ${p.status}` }))} />
                  <RollupList title="✍️ Contenus IA" testid="rollup-contents" items={rollup.contents.map((c, i) => ({
                    key: `${c.label}-${i}`, main: c.label, sub: `${c.kind}${c.channel ? " · " + c.channel : ""}` }))} />
                  <RollupList title="📣 Publications réseaux" testid="rollup-pubs" items={rollup.publications.map((p, i) => ({
                    key: `${p.angle_label}-${i}`, main: p.angle_label || "Publication", sub: `${p.status}${p.scheduled_at ? " · " + String(p.scheduled_at).slice(0, 10) : ""}` }))} />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* OBJECTIFS */}
        <section>
          <SectionTitle icon={Target} title="Objectifs KAIZEN" sub="Cible vs réel — modifiables via « Mes cibles »" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {objectives.map((o) => <ObjectiveCard key={o.key} o={o} pending={o.key === "engagement" && !perf?.configured} />)}
          </div>
        </section>

        {/* PILIERS DE CONTENU */}
        <section>
          <SectionTitle icon={ChartBar} title="Piliers de contenu" sub="Répartition des publications par message — assure-toi de couvrir tes priorités" />
          {data?.kids_gap && (
            <div data-testid="kids-gap-alert" className="hard-border p-4 mb-4 flex items-start gap-3" style={{ background: "#FFF6E5", borderColor: GOLD }}>
              <Warning size={22} weight="fill" style={{ color: GOLD }} className="mt-0.5" />
              <div className="text-sm">
                <strong>Angle mort détecté : « Enfants / Petits Samouraïs ».</strong> C'est ton segment le plus rentable et tu n'as aucune publication ciblée.
                Génère une série dans le <a href="/app/marketing-studio" className="underline font-semibold">Marketing Studio</a>.
              </div>
            </div>
          )}
          <div className="space-y-2">
            {(data?.pillars || []).map((p) => <PillarBar key={p.key} p={p} max={Math.max(...(data.pillars.map((x) => x.total)), 1)} />)}
            {(!data?.pillars || data.pillars.length === 0) && <Empty text="Aucune publication dans le pipeline. Génère ton calendrier dans Marketing Studio." />}
          </div>
        </section>

        {/* ACQUISITION */}
        <section>
          <SectionTitle icon={Users} title="Acquisition & Funnel" sub="Essais, inscriptions et conversion" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatBox label="Membres actifs" value={data?.acquisition?.active_total ?? "—"} />
            <StatBox label="Essais ce mois" value={data?.acquisition?.trials_month ?? "—"} accent />
            <StatBox label="Inscriptions (été)" value={data?.acquisition?.enroll_summer ?? "—"} accent />
            <StatBox label="Conversion leads" value={data ? `${data.acquisition.conversion_rate}%` : "—"} sub={`${data?.acquisition?.leads_converted ?? 0}/${data?.acquisition?.leads_total ?? 0}`} />
          </div>
          {data && Object.keys(data.acquisition.by_discipline || {}).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(data.acquisition.by_discipline).map(([d, n]) => (
                <span key={d} className="ticker px-3 py-1.5 hard-border bg-secondary/40">{d.replace(/_/g, " ")} · {n}</span>
              ))}
            </div>
          )}
        </section>

        {/* PERFORMANCE FB/IG */}
        <section>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <SectionTitle icon={TrendUp} title="Performance des publications" sub="Ce qui marche — et ce qui marche le moins (Facebook)" inline />
            <button data-testid="btn-ai-reco" onClick={() => loadPerf(true)} disabled={aiBusy || !perf?.configured}
              className="flex items-center gap-2 px-4 py-2.5 ticker text-white transition-colors disabled:opacity-50" style={{ background: RED }}>
              <Sparkle size={16} /> {aiBusy ? "Analyse IA…" : "Diagnostic IA (corriger/adapter)"}
            </button>
          </div>

          {loadingPerf && <Empty text="Chargement des statistiques Facebook…" />}

          {perf && !perf.configured && (
            <div className="hard-border p-6 flex items-center gap-3 bg-secondary/30" data-testid="perf-not-configured">
              <FacebookLogo size={28} className="text-[#1877F2]" />
              <div className="text-sm">{perf.message} <a href="/app/facebook" className="underline font-semibold">Aller à Facebook →</a></div>
            </div>
          )}

          {perf?.configured && (
            <div className="space-y-6" data-testid="perf-content">
              <div className="grid grid-cols-3 gap-4">
                <StatBox label="Posts analysés" value={perf.total_posts} />
                <StatBox label="Engagement moyen" value={perf.avg_engagement} accent />
                <StatBox label="Engagement (mois)" value={perf.engagement_total_month} accent />
              </div>

              {perf.recommendation && (
                <div className="hard-border p-5" style={{ background: "#FFF6E5", borderColor: GOLD }} data-testid="ai-recommendation">
                  <div className="ticker mb-2 flex items-center gap-2" style={{ color: "#8a6d24" }}><Sparkle size={14} weight="fill" /> Diagnostic IA</div>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed prose-sm" style={{ color: "#1c1917" }}>{perf.recommendation}</div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PostList title="🏆 Meilleurs posts" posts={perf.best} icon={Trophy} color={GOLD} testid="best-posts" />
                <PostList title="⚠️ À corriger (les moins performants)" posts={perf.worst} icon={Warning} color={RED} testid="worst-posts" />
              </div>
            </div>
          )}
        </section>
      </div>

      {/* MODALE CIBLES */}
      {goalsOpen && goals && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={() => setGoalsOpen(false)}>
          <div className="bg-background hard-border max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="goals-modal">
            <div className="hard-border-b px-5 py-4">
              <div className="ticker text-muted-foreground">Personnaliser</div>
              <div className="font-display font-bold text-lg">Mes cibles marketing</div>
            </div>
            <div className="p-5 space-y-4">
              {GOAL_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="ticker text-muted-foreground block mb-2">{f.label}</label>
                  <input type="number" min={0} data-testid={`goal-${f.key}`} value={goals[f.key]}
                    onChange={(e) => setGoals({ ...goals, [f.key]: parseInt(e.target.value || "0", 10) })}
                    className="w-full hard-border px-4 py-2.5" />
                </div>
              ))}
            </div>
            <div className="hard-border-t p-5 flex justify-between">
              <button onClick={() => setGoalsOpen(false)} className="ticker hover:text-[#D90429]">Annuler</button>
              <button data-testid="save-goals" onClick={saveGoals} className="px-6 py-2.5 ticker text-zinc-950" style={{ background: GOLD }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, sub, inline }) {
  return (
    <div className={inline ? "" : "mb-4"}>
      <div className="flex items-center gap-2">
        <Icon size={20} weight="duotone" style={{ color: RED }} />
        <h2 className="font-display font-bold text-xl">{title}</h2>
      </div>
      {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function ObjectiveCard({ o, pending }) {
  const pct = pending ? 0 : o.progress_pct;
  const reached = pct >= 100;
  return (
    <div className="hard-border p-5 bg-white" data-testid={`objective-${o.key}`}>
      <div className="ticker text-muted-foreground">{o.sub}</div>
      <div className="font-display font-bold text-base mt-1 leading-tight">{o.label}</div>
      <div className="flex items-end gap-1.5 mt-3">
        <span className="font-display font-black text-3xl">{pending ? "…" : o.actual}</span>
        <span className="text-muted-foreground text-sm mb-1">/ {o.target} {o.unit}</span>
      </div>
      <div className="h-2 bg-secondary mt-3 overflow-hidden">
        <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: reached ? "#16a34a" : GOLD }} />
      </div>
      <div className="ticker mt-1.5" style={{ color: reached ? "#16a34a" : "#6b7280" }}>{pending ? "En attente Facebook" : `${pct}% atteint`}</div>
    </div>
  );
}

function PillarBar({ p, max }) {
  const pct = Math.round((p.total / max) * 100);
  return (
    <div className="hard-border p-4 flex items-center gap-4 bg-white" data-testid={`pillar-${p.key}`}>
      <div className="text-2xl w-8 text-center">{p.emoji}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-sm truncate">{p.label}</span>
          <span className="ticker text-muted-foreground ml-2 whitespace-nowrap">{p.published} publiés · {p.scheduled} prévus</span>
        </div>
        <div className="h-2 bg-secondary mt-2 overflow-hidden">
          <div className="h-full" style={{ width: `${pct}%`, background: RED }} />
        </div>
        {p.objective && <div className="ticker text-muted-foreground mt-1">{p.objective}</div>}
      </div>
      <div className="font-display font-black text-2xl">{p.total}</div>
    </div>
  );
}

function PostList({ title, posts, icon: Icon, color, testid }) {
  return (
    <div data-testid={testid}>
      <div className="ticker mb-3 flex items-center gap-2" style={{ color }}><Icon size={16} weight="fill" /> {title}</div>
      <div className="space-y-2">
        {(!posts || posts.length === 0) && <Empty text="Aucune donnée." />}
        {posts?.map((p) => (
          <div key={p.id} className="hard-border p-3 flex gap-3 bg-white">
            {p.image && <img src={p.image} alt="" className="w-14 h-14 object-cover hard-border flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm line-clamp-2">{p.excerpt}</div>
              <div className="flex items-center gap-3 mt-2 text-muted-foreground" style={{ fontSize: "12px" }}>
                <span className="flex items-center gap-1"><Heart size={13} /> {p.likes}</span>
                <span className="flex items-center gap-1"><ChatCircle size={13} /> {p.comments}</span>
                <span className="flex items-center gap-1"><ShareFat size={13} /> {p.shares}</span>
                <span className="font-bold ml-auto" style={{ color }}>{p.engagement} total</span>
                {p.permalink_url && <a href={p.permalink_url} target="_blank" rel="noreferrer" className="hover:opacity-70"><ArrowUpRight size={14} /></a>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatBox({ label, value, sub, accent }) {
  return (
    <div className="hard-border p-4 bg-white" data-testid={`stat-${label}`}>
      <div className="ticker text-muted-foreground">{label}</div>
      <div className="font-display font-black text-3xl mt-1" style={accent ? { color: RED } : {}}>{value}</div>
      {sub && <div className="ticker text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Empty({ text }) {
  return <div className="hard-border p-6 text-center text-sm text-muted-foreground bg-secondary/20">{text}</div>;
}

function RollupStat({ label, value, accent }) {
  return (
    <div className="hard-border p-3 bg-white text-center" data-testid={`rollup-stat-${label}`}>
      <div className="font-display font-black text-2xl" style={accent ? { color: RED } : {}}>{value}</div>
      <div className="ticker text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function RollupList({ title, items, testid }) {
  return (
    <div className="hard-border p-4 bg-white" data-testid={testid}>
      <div className="ticker mb-3 font-semibold">{title} <span className="text-muted-foreground">({items.length})</span></div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground">Aucun élément lié.</div>
      ) : (
        <ul className="space-y-2 max-h-64 overflow-y-auto">
          {items.slice(0, 20).map((it) => (
            <li key={it.key} className="border-b border-zinc-100 pb-2 last:border-0">
              <div className="text-sm font-medium text-zinc-900 truncate">{it.main}</div>
              <div className="ticker text-muted-foreground">{it.sub}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

